import { Component, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { AuthService } from '../auth.service';
import { PasswordResetFlowService } from '../password-reset-flow.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    ToastModule
  ],
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  providers: [MessageService]
})
export class ChangePasswordComponent implements OnDestroy {
  loginForm: FormGroup;
  submitted = false;
  loading = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService,
    private http: HttpClient,
    private resetFlow: PasswordResetFlowService
  ) {
    this.loginForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      passwordRepited: ['', [Validators.required, Validators.minLength(8)]]
    }, { validators: this.passwordsMatchValidator });

    this.subscriptions.push(
      this.loginForm.get('password')!.valueChanges.subscribe(() => {
        this.loginForm.updateValueAndValidity();
      }),
      this.loginForm.get('passwordRepited')!.valueChanges.subscribe(() => {
        this.loginForm.updateValueAndValidity();
      })
    );
  }

  passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password');
    const passwordRepited = group.get('passwordRepited');
    if (!password || !passwordRepited) return null;

    if (password.value !== passwordRepited.value) {
      passwordRepited.setErrors({ ...(passwordRepited.errors || {}), passwordsMismatch: true });
      return { passwordsMismatch: true };
    } else {
      if (passwordRepited.errors) {
        const { passwordsMismatch, ...otherErrors } = passwordRepited.errors;
        if (Object.keys(otherErrors).length === 0) {
          passwordRepited.setErrors(null);
        } else {
          passwordRepited.setErrors(otherErrors);
        }
      }
      return null;
    }
  }

  get f() { return this.loginForm.controls; }

  onSubmit() {
    this.submitted = true;

    if (this.loginForm.invalid) return;

    const email = localStorage.getItem('emailVerifyForgotPassword');
    const code = this.resetFlow.getCode();
    if (!email || !code) {
      // El código vive solo en memoria: tras recargar hay que verificarlo otra vez.
      this.messageService.add({
        severity: 'error',
        summary: 'Verificación vencida',
        detail: 'Vuelve a ingresar el código que te enviamos al correo.'
      });
      setTimeout(() => this.router.navigate(['/forgot-password']), 2000);
      return;
    }

    this.loading = true;
    const { password } = this.loginForm.value;

    this.http.post<any>(`${environment.apiUrlMSIdentifyService}/auth/reset-password`, {
      email,
      newPassword: password,
      code,
    }).subscribe({
      next: (res) => {
        if (res.success) {
          this.resetFlow.clear();
          localStorage.removeItem('emailVerifyForgotPassword');
          this.messageService.add({
            severity: 'success',
            summary: res.message,
            detail: 'Ahora puedes iniciar sesión con tu nueva contraseña.'
          });
          setTimeout(() => {
            this.router.navigate(['/']);
          }, 2000);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: res.message || 'No se pudo restablecer la contraseña.'
          });
        }
        this.loading = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'No se pudo restablecer la contraseña.'
        });
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    // Al salir del paso (con o sin éxito) el código no queda en memoria.
    this.resetFlow.clear();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}